import { Crosshair } from 'lucide-react';
import { Card } from '@/components/ui/card';
import type { Player, HitData } from '@/types/game';
import { getTeamColors, teamColors } from '@/utils/game-utils';

interface HitMatrixProps {
  hitData: HitData[];
  players: Player[];
}

export const HitMatrix = ({ hitData, players }: HitMatrixProps) => {
  if (!hitData || hitData.length === 0) return null;

  const sortedByWeste = [...players].sort((a, b) => parseInt(a.weste) - parseInt(b.weste));

  return (
    <Card className="p-3 sm:p-6 border-2 border-primary/20 bg-card/50 backdrop-blur-sm animate-slide-up">
      <h2 className="text-lg sm:text-2xl font-bold mb-4 sm:mb-6 text-center flex items-center justify-center space-x-2">
        <Crosshair className="w-5 h-5 sm:w-6 sm:h-6 text-neon-red" />
        <span>Treffertabelle</span>
      </h2>
      <div className="overflow-x-auto -mx-3 sm:mx-0">
        <table className="w-full border-collapse min-w-max">
          <thead>
            <tr>
              <th className="border border-border p-1 sm:p-2 text-left font-bold text-xs sm:text-sm sticky left-0 bg-card z-10">Spieler</th>
              {sortedByWeste.map((player) => (
                <th key={player.weste} className="border border-border p-1 sm:p-2 text-center font-bold text-xs whitespace-nowrap">
                  <span className="hidden sm:inline">{player.name}</span>
                  <span className="sm:hidden">W{player.weste}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {hitData.map((shooter, index) => {
              const shooterPlayer = players.find(p => p.name === shooter.player);
              const shooterColors = shooterPlayer
                ? (teamColors[shooterPlayer.team as keyof typeof teamColors] || { bg: 'bg-muted/20', text: 'text-foreground', border: 'border-muted', glow: 'shadow-md' })
                : { bg: 'bg-muted/20', text: 'text-foreground', border: 'border-muted', glow: 'shadow-md' };

              return (
                <tr key={index} className="hover:bg-muted/20">
                  <td className={`border border-border p-1 sm:p-2 font-medium text-xs sm:text-sm ${shooterColors.text} sticky left-0 bg-card z-10`}>
                    <span className="hidden sm:inline">{shooter.player}</span>
                    <span className="sm:hidden truncate block max-w-[80px]">{shooter.player.split(' ')[0]}</span>
                  </td>
                  {sortedByWeste.map((target) => {
                    const hits = shooter.targets[target.weste] || 0;
                    const colors = getTeamColors(target.team);
                    return (
                      <td
                        key={target.weste}
                        className={`border border-border p-1 sm:p-2 text-center text-xs sm:text-sm ${hits > 0 ? colors.text + ' font-bold' : 'text-muted-foreground'}`}
                      >
                        {hits > 0 ? hits : '-'}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
};
